import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import Header from "@amzn/awsui-components-react-v3/polaris/header";
import { StatusIndicator } from "@amzn/awsui-components-react-v3";
import { StatusIndicatorProps } from "@amzn/awsui-components-react-v3";
import Button from "@amzn/awsui-components-react-v3/polaris/button";
import Container from "@amzn/awsui-components-react-v3/polaris/container";
import ColumnLayout from "@amzn/awsui-components-react-v3/polaris/column-layout";
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react-v3/polaris/space-between";
import Alert from "@amzn/awsui-components-react-v3/polaris/alert";
import Link from "@amzn/awsui-components-react-v3/polaris/link";
import CopyToClipboard from "@amzn/awsui-components-react-v3/polaris/copy-to-clipboard";
import BreadcrumbGroup from "@amzn/awsui-components-react-v3/polaris/breadcrumb-group";
import { AppLayout, Flashbar } from "@amzn/awsui-components-react";
import { getApiUrl, verify, getOptions } from "../../../auth/login.js";
import { SANDBOX_STATUS, stringToSandboxStatus } from "../../../constants/constants";
import SideBar from "../../../navigation/sidebar.js";
import AppContext from "../../../context/AppContext";
import { getSidebarItemFromPageStage } from "../../home/home";
import { formatRelativeTime } from "../../../util/relativeTime";
import { DeleteSandboxConfirmationModal } from "../table/delete-confirmation-modal";

const TRANSITIONAL_STATUSES = [
    SANDBOX_STATUS.CREATING,
    SANDBOX_STATUS.STARTING,
    SANDBOX_STATUS.STOPPING,
    SANDBOX_STATUS.UPDATING,
    SANDBOX_STATUS.DELETING,
];

const POLL_INTERVAL_TRANSITIONAL = 10;
const POLL_INTERVAL_STABLE = 30;

interface MatchParams {
    sandboxId: string;
}

interface SandboxInfo {
    sandboxId: string;
    sandboxStatus: string;
    sandboxPlatformId: string;
    sandboxStatusReason?: string;
    lastCfnFailureCause?: string;
    lastOperationTimestamp?: string;
    lastStatusMessage?: string;
    sandboxProperties: {
        sandboxInstanceType: string;
        diskSizeInGB: number;
    };
    sandboxLifeCycleConfig: {
        idleTimeToStopInMin: number;
        idleCPUThreshold: number;
    };
}

interface SandboxDetailState {
    sandbox: SandboxInfo | null;
    loading: boolean;
    error: string | null;
    notFound: boolean;
    flashMessage: string | null;
    flashType: "success" | "error" | null;
    countdown: number;
    deleteSandboxModalVisible: boolean;
}

type Props = RouteComponentProps<MatchParams>;

function getStatusIndicatorType(status: SANDBOX_STATUS, errorMsg?: string): StatusIndicatorProps.Type {
    switch (status) {
        case SANDBOX_STATUS.STOPPED:
            return errorMsg ? "error" : "stopped";
        case SANDBOX_STATUS.IN_SERVICE:
            return "success";
        case SANDBOX_STATUS.FAILED:
            return "error";
        case SANDBOX_STATUS.IN_MAINTENANCE:
        case SANDBOX_STATUS.UNKNOWN:
            return "warning";
        default:
            return "in-progress";
    }
}

function isTransitional(status: string): boolean {
    const parsed = stringToSandboxStatus(status);
    return TRANSITIONAL_STATUSES.includes(parsed);
}

class SandboxDetailPage extends Component<Props, SandboxDetailState> {
    static contextType = AppContext;
    pollTimer: any;
    countdownTimer: any;
    _isMounted: boolean;

    constructor(props: Props) {
        super(props);
        this.pollTimer = null;
        this.countdownTimer = null;
        this._isMounted = false;
        this.state = {
            sandbox: null,
            loading: true,
            error: null,
            notFound: false,
            flashMessage: null,
            flashType: null,
            countdown: 0,
            deleteSandboxModalVisible: false,
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.fetchSandbox();
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.clearPoll();
        this.clearCountdown();
    }

    clearPoll() {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    clearCountdown() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
        }
    }

    getIntervalSeconds(): number {
        return this.state.sandbox && isTransitional(this.state.sandbox.sandboxStatus)
            ? POLL_INTERVAL_TRANSITIONAL
            : POLL_INTERVAL_STABLE;
    }

    schedulePoll() {
        this.clearPoll();
        this.clearCountdown();

        const intervalSeconds = this.getIntervalSeconds();

        if (this._isMounted) {
            this.setState({ countdown: intervalSeconds });
        }

        this.countdownTimer = setInterval(() => {
            if (!this._isMounted) {
                this.clearCountdown();
                return;
            }
            const next = this.state.countdown - 1;
            if (next <= 0) {
                this.clearCountdown();
                this.setState({ countdown: 0 });
                this.fetchSandbox();
            } else {
                this.setState({ countdown: next });
            }
        }, 1000);
    }

    fetchSandbox() {
        this.clearCountdown();
        const sandboxId = this.props.match.params.sandboxId;
        const user = localStorage.getItem("userId");
        const account = localStorage.getItem("account");

        if (!user || !account) {
            this.setState({ error: "Not authenticated. Please log in.", loading: false });
            return;
        }

        verify(async () => {
            const opts = await getOptions();
            opts.method = "GET";
            try {
                const res = await fetch(
                    `${await getApiUrl()}/list_sandboxes?user_alias=${user}&aws_account_id=${account}&sandbox_id=${sandboxId}`,
                    opts
                );
                if (!res.ok) {
                    if (this._isMounted) {
                        this.setState({ error: "Failed to fetch sandbox data.", loading: false });
                        this.schedulePoll();
                    }
                    return;
                }
                const data = await res.json();
                const sandboxes: SandboxInfo[] = data.sandboxInfos || [];
                const sandbox = sandboxes.find((s) => s.sandboxId === sandboxId) || null;

                if (this._isMounted) {
                    if (sandbox) {
                        this.setState({ sandbox, loading: false, error: null, notFound: false });
                        this.schedulePoll();
                    } else {
                        this.setState({ sandbox: null, loading: false, error: null, notFound: true });
                    }
                }
            } catch (err) {
                if (this._isMounted) {
                    this.setState({ error: "Failed to fetch sandbox data.", loading: false });
                    this.schedulePoll();
                }
            }
        });
    }

    performAction(api: string, successMsg: string) {
        const sandbox = this.state.sandbox;
        if (!sandbox) return;

        const user = localStorage.getItem("userId");
        const account = localStorage.getItem("account");

        this.setState({ loading: true, flashMessage: null, flashType: null });

        verify(async () => {
            const opts = await getOptions();
            opts.method = "POST";
            opts.body = JSON.stringify({
                awsAccountId: account,
                userAlias: user,
                sandboxId: sandbox.sandboxId,
            });
            try {
                const res = await fetch(`${await getApiUrl()}/${api}`, opts);
                if (res.ok) {
                    if (this._isMounted) {
                        this.setState({ flashMessage: successMsg, flashType: "success", loading: false });
                        this.fetchSandbox();
                    }
                } else {
                    const body = await res.json().catch(() => null);
                    const msg = (body && body.message) || "Operation failed. Please try again.";
                    if (this._isMounted) {
                        this.setState({ flashMessage: msg, flashType: "error", loading: false });
                    }
                }
            } catch (err) {
                if (this._isMounted) {
                    this.setState({ flashMessage: "Operation failed. Please try again.", flashType: "error", loading: false });
                }
            }
        });
    }

    renderValue(value: any): string {
        if (value === null || value === undefined || value === "") return "--";
        return String(value);
    }

    deleteSandbox() {
        const sandbox = this.state.sandbox;
        if (!sandbox) return;

        const user = localStorage.getItem("userId");
        const account = localStorage.getItem("account");

        if (!user || !account) {
            this.setState({ flashMessage: "Not authenticated. Please log in.", flashType: "error" });
            return;
        }

        this.setState({ flashMessage: null, flashType: null });

        verify(async () => {
            const opts = await getOptions();
            opts.method = "POST";
            opts.body = JSON.stringify({
                awsAccountId: account,
                userAlias: user,
                sandboxId: sandbox.sandboxId,
            });
            try {
                const res = await fetch(`${await getApiUrl()}/delete_sandbox`, opts);
                if (res.ok) {
                    if (this._isMounted) {
                        this.setState({ flashMessage: `Deleting sandbox ${sandbox.sandboxId}...`, flashType: "success" });
                        this.fetchSandbox();
                    }
                } else {
                    if (this._isMounted) {
                        this.setState({
                            flashMessage: `Failed to delete sandbox ${sandbox.sandboxId}. Please try again.`,
                            flashType: "error",
                        });
                    }
                }
            } catch (err) {
                if (this._isMounted) {
                    this.setState({
                        flashMessage: `Failed to delete sandbox ${sandbox.sandboxId}. Please try again.`,
                        flashType: "error",
                    });
                }
            }
        });
    }

    openEndpoint(jupyterlab: boolean) {
        const sandbox = this.state.sandbox;
        if (!sandbox) return;

        const user = localStorage.getItem("userId");
        const account = localStorage.getItem("account");

        if (!user || !account) {
            this.setState({ flashMessage: "Not authenticated. Please log in.", flashType: "error" });
            return;
        }

        // Open synchronously within the click handler to avoid popup blocker
        const newWindow = window.open("", "_blank");
        if (!newWindow) {
            this.setState({ flashMessage: "Popup blocked. Please allow popups for this site.", flashType: "error" });
            return;
        }

        verify(async () => {
            const opts = await getOptions();
            opts.method = "GET";
            try {
                const res = await fetch(
                    `${await getApiUrl()}/get_sandbox_endpoint_url?user_alias=${user}&aws_account_id=${account}&sandbox_id=${sandbox.sandboxId}`,
                    opts
                );
                if (res.ok) {
                    const data = await res.json();
                    const url = `${data.signedSandboxEndpointUrl}${jupyterlab ? "&view=lab" : ""}`;
                    if (newWindow) {
                        newWindow.location.href = url;
                    }
                } else {
                    if (newWindow) newWindow.close();
                    if (this._isMounted) {
                        this.setState({ flashMessage: "Endpoint cannot be reached. Please try again.", flashType: "error" });
                    }
                }
            } catch (err) {
                if (newWindow) newWindow.close();
                if (this._isMounted) {
                    this.setState({ flashMessage: "Endpoint cannot be reached. Please try again.", flashType: "error" });
                }
            }
        });
    }

    renderDetails() {
        const sandbox = this.state.sandbox;
        if (!sandbox) return null;

        return (
            <Container header={<Header variant="h2">Sandbox details</Header>}>
                <ColumnLayout columns={2} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">Instance type</Box>
                        <div>
                            {this.renderValue(sandbox.sandboxProperties?.sandboxInstanceType)}
                            {sandbox.sandboxProperties?.sandboxInstanceType && (
                                <>{" "}<CopyToClipboard variant="icon" textToCopy={sandbox.sandboxProperties.sandboxInstanceType} copyButtonAriaLabel="Copy instance type" copySuccessText="Instance type copied" copyErrorText="Failed to copy" /></>
                            )}
                        </div>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Platform</Box>
                        <div>
                            {this.renderValue(sandbox.sandboxPlatformId)}
                            {sandbox.sandboxPlatformId && (
                                <>{" "}<CopyToClipboard variant="icon" textToCopy={sandbox.sandboxPlatformId} copyButtonAriaLabel="Copy platform" copySuccessText="Platform copied" copyErrorText="Failed to copy" /></>
                            )}
                        </div>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Disk size (GB)</Box>
                        <div>
                            {this.renderValue(sandbox.sandboxProperties?.diskSizeInGB)}
                            {sandbox.sandboxProperties?.diskSizeInGB != null && (
                                <>{" "}<CopyToClipboard variant="icon" textToCopy={String(sandbox.sandboxProperties.diskSizeInGB)} copyButtonAriaLabel="Copy disk size" copySuccessText="Disk size copied" copyErrorText="Failed to copy" /></>
                            )}
                        </div>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Idle time (min)</Box>
                        <div>
                            {this.renderValue(sandbox.sandboxLifeCycleConfig?.idleTimeToStopInMin)}
                            {sandbox.sandboxLifeCycleConfig?.idleTimeToStopInMin != null && (
                                <>{" "}<CopyToClipboard variant="icon" textToCopy={String(sandbox.sandboxLifeCycleConfig.idleTimeToStopInMin)} copyButtonAriaLabel="Copy idle time" copySuccessText="Idle time copied" copyErrorText="Failed to copy" /></>
                            )}
                        </div>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Idle CPU threshold (%)</Box>
                        <div>
                            {this.renderValue(sandbox.sandboxLifeCycleConfig?.idleCPUThreshold)}
                            {sandbox.sandboxLifeCycleConfig?.idleCPUThreshold != null && (
                                <>{" "}<CopyToClipboard variant="icon" textToCopy={String(sandbox.sandboxLifeCycleConfig.idleCPUThreshold)} copyButtonAriaLabel="Copy CPU threshold" copySuccessText="CPU threshold copied" copyErrorText="Failed to copy" /></>
                            )}
                        </div>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">AWS Account</Box>
                        <div>
                            {this.renderValue(localStorage.getItem("account"))}
                            {localStorage.getItem("account") && (
                                <>{" "}<CopyToClipboard variant="icon" textToCopy={localStorage.getItem("account") || ""} copyButtonAriaLabel="Copy account ID" copySuccessText="Account ID copied" copyErrorText="Failed to copy" /></>
                            )}
                        </div>
                    </div>
                </ColumnLayout>
            </Container>
        );
    }

    renderActivity() {
        const sandbox = this.state.sandbox;
        if (!sandbox) return null;

        const status = stringToSandboxStatus(sandbox.sandboxStatus);
        const transitional = TRANSITIONAL_STATUSES.includes(status);
        const failed = status === SANDBOX_STATUS.FAILED;
        const { countdown } = this.state;

        return (
            <Container header={<Header variant="h2">Activity</Header>}>
                <SpaceBetween size="s">
                    {transitional && (
                        <SpaceBetween size="xs">
                            <StatusIndicator type="in-progress">
                                {sandbox.lastStatusMessage || `Sandbox is ${sandbox.sandboxStatus.toLowerCase()}...`}
                            </StatusIndicator>
                            {sandbox.lastOperationTimestamp && (
                                <Box variant="small" color="text-body-secondary">
                                    Last operation: {formatRelativeTime(sandbox.lastOperationTimestamp)}
                                </Box>
                            )}
                        </SpaceBetween>
                    )}
                    {failed && (
                        <Alert type="error">
                            {sandbox.lastCfnFailureCause || sandbox.sandboxStatusReason || "Sandbox operation failed."}
                        </Alert>
                    )}
                    {!transitional && !failed && (
                        <SpaceBetween size="xs">
                            {sandbox.lastStatusMessage && (
                                <Box color="text-body-secondary">{sandbox.lastStatusMessage}</Box>
                            )}
                            {sandbox.lastOperationTimestamp && (
                                <Box variant="small" color="text-body-secondary">
                                    Last operation: {formatRelativeTime(sandbox.lastOperationTimestamp)}
                                </Box>
                            )}
                            {!sandbox.lastStatusMessage && !sandbox.lastOperationTimestamp && (
                                <Box color="text-body-secondary">No operations in progress.</Box>
                            )}
                        </SpaceBetween>
                    )}
                    {countdown > 0 && (
                        <Box variant="small" color="text-status-inactive">
                            Next update in {countdown} seconds
                        </Box>
                    )}
                </SpaceBetween>
            </Container>
        );
    }

    render() {
        const { sandbox, loading, error, notFound, flashMessage, flashType, deleteSandboxModalVisible } = this.state;
        const sandboxId = this.props.match.params.sandboxId;

        const status = sandbox ? stringToSandboxStatus(sandbox.sandboxStatus) : null;
        const statusType: StatusIndicatorProps.Type = status !== null
            ? getStatusIndicatorType(status, sandbox ? sandbox.sandboxStatusReason : undefined)
            : "info";
        const canStart = status === SANDBOX_STATUS.STOPPED;
        const canStop = status === SANDBOX_STATUS.IN_SERVICE;
        const canDelete = status === SANDBOX_STATUS.STOPPED || status === SANDBOX_STATUS.FAILED;
        const canUpdate = status === SANDBOX_STATUS.STOPPED || status === SANDBOX_STATUS.FAILED;
        const canConnect = status === SANDBOX_STATUS.IN_SERVICE;
        const transitional = sandbox ? isTransitional(sandbox.sandboxStatus) : false;

        const content = (
            <SpaceBetween size="l">
                <BreadcrumbGroup
                    items={[
                        { text: "Sandboxes", href: "/sandboxes" },
                        { text: sandboxId, href: "#" },
                    ]}
                />

                {flashMessage && (
                    <Flashbar
                        items={[{
                            type: flashType === "error" ? "error" : "success",
                            content: flashMessage,
                            dismissible: true,
                            onDismiss: () => this.setState({ flashMessage: null, flashType: null }),
                        }]}
                    />
                )}

                {error && !sandbox && !notFound && (
                    <Alert type="error">{error}</Alert>
                )}

                {notFound && (
                    <Alert type="info">
                        This sandbox no longer exists. <Link href="/sandboxes">View your sandboxes</Link> to continue.
                    </Alert>
                )}

                {sandbox && (
                    <>
                        <Header
                            variant="h1"
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button
                                        iconName="refresh"
                                        variant="icon"
                                        disabled={loading}
                                        ariaLabel="Refresh sandbox status"
                                        onClick={() => this.fetchSandbox()}
                                    />
                                    <Button
                                        variant="primary"
                                        disabled={!canConnect || transitional || loading}
                                        onClick={() => this.openEndpoint(true)}
                                    >
                                        JupyterLab
                                    </Button>
                                    <Button
                                        disabled={!canConnect || transitional || loading}
                                        onClick={() => this.openEndpoint(false)}
                                    >
                                        Jupyter
                                    </Button>
                                    <Button
                                        disabled={!canUpdate || transitional || loading}
                                        href={`/update/${sandboxId}`}
                                    >
                                        Update
                                    </Button>
                                    <Button
                                        disabled={!canStart || transitional || loading}
                                        onClick={() => this.performAction("start_sandbox", "Sandbox is starting.")}
                                    >
                                        Start
                                    </Button>
                                    <Button
                                        disabled={!canStop || transitional || loading}
                                        onClick={() => this.performAction("stop_sandbox", "Sandbox is stopping.")}
                                    >
                                        Stop
                                    </Button>
                                    <Button
                                        disabled={!canDelete || transitional || loading}
                                        onClick={() => this.setState({ deleteSandboxModalVisible: true })}
                                    >
                                        Delete
                                    </Button>
                                </SpaceBetween>
                            }
                        >
                            {sandboxId}
                            {" "}
                            <CopyToClipboard
                                variant="icon"
                                textToCopy={sandboxId}
                                copyButtonAriaLabel="Copy sandbox ID"
                                copySuccessText="Sandbox ID copied"
                                copyErrorText="Failed to copy"
                            />
                            {" "}
                            <StatusIndicator type={statusType}>
                                {sandbox.sandboxStatus}
                            </StatusIndicator>
                        </Header>

                        {this.renderDetails()}
                        {this.renderActivity()}

                        <DeleteSandboxConfirmationModal
                            deleteSandbox={() => this.deleteSandbox()}
                            visible={deleteSandboxModalVisible}
                            setVisible={(visible: boolean) => this.setState({ deleteSandboxModalVisible: visible })}
                            sandboxId={sandboxId}
                        />
                    </>
                )}

                {loading && !sandbox && !notFound && (
                    <Box textAlign="center" padding="l">Loading sandbox details...</Box>
                )}
            </SpaceBetween>
        );

        return (
            <AppLayout
                content={content}
                navigation={<SideBar activeHref="/sandboxes" items={getSidebarItemFromPageStage(this.context.pageStage)} />}
                navigationOpen={true}
                onNavigationChange={() => {}}
                toolsHide={true}
            />
        );
    }
}

export default SandboxDetailPage;
